// DispatchDeck — Effective-dated pay rates (TASK-1558)
//
// PURE math module: no Firebase, no DOM, no AppState. Loaded in the browser via
// ONE side-effect import from clock.js (`import '../payRates.js?v=…'`) which
// sets window.PayRates — every other module reads window.PayRates at call time
// (Pitfall #1: never a second `?v=` import). Node unit tests require() it
// directly (UMD): `node --test tests/pay-rates.test.mjs`.
//
// WHY THIS EXISTS. A person doc holds ONE hrHourlyRate/hrAnnualSalary with no
// effective date. Every payroll surface applied that single current value to
// every hour in the period — including hours worked before the rate existed.
// On the TITS Jul 27–Aug 9 packet, 4 of 17 people were wrong (Ivan $176.70
// overpaid on a FINAL check; Brian's salary→hourly conversion hid a 53.5h OT
// week). This module resolves the rate in effect AT EACH MOMENT from an
// append-only history and prices multi-rate workweeks per 29 CFR 778.115
// (weighted-average regular rate — OT premium rides neither endpoint rate).
//
// DATA SHAPE (tenants/{tid}/payRates/{personId}, written by hr.js):
//   { personId, history: [ {
//       effectiveFrom: Timestamp|ms|ISO|null,  // null = baseline "since before history began"
//       employmentType: 'salary'|'hourly'|null,
//       hourlyRate: number|null, annualSalary: number|null,
//       perDiem: number|null, payRate: number|null, payRateUnit: string|null,
//       setBy: email, setAt: Timestamp, source: 'baseline'|'hr-edit-employment'|'hr-comp-review'
//   } ] }
// History is APPEND-ONLY (arrayUnion; rules enforce non-shrinking). The current
// rate = the entry with the latest effectiveFrom <= now. The person doc's
// hrHourlyRate/hrAnnualSalary stay authoritative for "current" display; history
// exists so payroll can resolve PAST moments.
//
// DELIBERATE NON-GOALS:
// • clockEntries.rate is NOT the pay snapshot — it is the customer BILLING rate
//   (determineRoleAndRate: contract line items), except manual entries which
//   stamp hrHourlyRate. Extending it would conflate billing with payroll
//   (checked per the TASK-1558 spec lead; supersedes TASK-1483's snapshot item).
// • No backfill of historical rates from auditLog — fix-forward only. Periods
//   before the first history entry resolve to the seeded baseline (the value
//   the person doc held when history began).
// • Per-diem rate changes are WARNED (changesInWindow) but the per-diem dollar
//   auto-calc still uses the current hrPerDiem — effective-dating per-diem is
//   out of scope; the warning makes it a visible decision.
//
// DECIDED RULES (documented per the spec's edge cases):
// • Mid-shift change: the entry's hours are split at the exact effectiveFrom
//   timestamp, allocated proportionally to wall-clock time. Never rounded to a
//   day (both real TITS cases landed mid-shift).
// • Multi-rate workweek OT (29 CFR 778.115): per WORKWEEK, regular rate =
//   (Σ segment hours × segment rate + salary dollars attributed to the week)
//   ÷ total week hours; OT premium = 0.5 × regular rate × that week's OT hours.
//   All hours are also paid straight-time at their own segment rate. OT HOURS
//   come from computeCrossPeriodOT (clock.js) — the single source of truth for
//   OT hour attribution (83h-bug invariant) — this module only PRICES them.
// • Salary→hourly conversion: a workweek is exempt only if EVERY worked hour of
//   it was under salary basis. The conversion week is NON-exempt (it ends
//   hourly): all its hours count toward OT, and the salaried span's dollars
//   join the weighted-average numerator. Salary dollars for the period =
//   (annualSalary ÷ pay periods per year) × (salaried workdays ÷ workdays in
//   period); workday = Mon–Fri; the conversion day counts 0.5 if the switch
//   landed mid-day. (Reproduces the hand-closed Brian packet: $3,909.78.)
// • Hourly→salary conversion, >1 basis flip in one period, or piece-rate:
//   supported:false + a note — the UI flags "review by hand" instead of
//   guessing (flag, never fabricate).
// • A rate entered AFTER period close (effectiveFrom after the period) never
//   reaches back: resolution is by effectiveFrom, and the seeded baseline
//   covers the time before the first entry (the Steven case).
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && typeof root === 'object') root.PayRates = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const PERIODS_PER_YEAR = { weekly: 52, biweekly: 26, semimonthly: 24, monthly: 12 };
  const EPS = 1e-9;

  // ── time coercion ─────────────────────────────────────────────────────────
  // Accepts Firestore Timestamp, serialized {seconds}, ms number, ISO string,
  // Date, or null. Returns ms or null.
  function toMs(v) {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v.getTime();
    if (typeof v.toDate === 'function') { const d = v.toDate(); return isNaN(d.getTime()) ? null : d.getTime(); }
    if (typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    if (typeof v === 'string') { const d = new Date(v); return isNaN(d.getTime()) ? null : d.getTime(); }
    return null;
  }

  function num(v) {
    if (v === null || v === undefined || v === '') return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }

  // ── basis ─────────────────────────────────────────────────────────────────
  function basisFromEntry(e) {
    return {
      employmentType: e.employmentType || null,
      hourlyRate: num(e.hourlyRate),
      annualSalary: num(e.annualSalary),
      perDiem: num(e.perDiem),
      payRate: num(e.payRate),
    };
  }

  function basisFromPerson(person) {
    const p = person || {};
    return {
      employmentType: p.hrEmploymentType || null,
      hourlyRate: num(p.hrHourlyRate),
      annualSalary: num(p.hrAnnualSalary),
      perDiem: num(p.hrPerDiem),
      payRate: num(p.payRate),
    };
  }

  function isSalaryBasis(b) { return !!b && b.employmentType === 'salary'; }

  function basisEquals(a, b) {
    return a.employmentType === b.employmentType
      && a.hourlyRate === b.hourlyRate
      && a.annualSalary === b.annualSalary
      && a.perDiem === b.perDiem
      && a.payRate === b.payRate;
  }

  // ── history ───────────────────────────────────────────────────────────────
  // Sorted ascending; baseline (effectiveFrom null) first as effMs=-Infinity.
  // Ties on effectiveFrom break by setAt (later edit wins).
  function normalizeHistory(raw) {
    const arr = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.history) ? raw.history : []);
    // Idempotent: an already-normalized entry ({effMs, basis}) passes through.
    if (arr.length && arr[0] && arr[0].basis && ('effMs' in arr[0])) return arr;
    return arr
      .map(e => ({
        effMs: e.effectiveFrom === null || e.effectiveFrom === undefined ? -Infinity : toMs(e.effectiveFrom),
        setAtMs: toMs(e.setAt) || 0,
        source: e.source || null,
        setBy: e.setBy || null,
        basis: basisFromEntry(e),
      }))
      .filter(e => e.effMs === -Infinity || e.effMs !== null)
      .sort((a, b) => (a.effMs - b.effMs) || (a.setAtMs - b.setAtMs));
  }

  // The basis in effect at tMs: latest entry with effMs <= tMs. With history but
  // tMs before the first entry, use the EARLIEST entry (closest known to the
  // historical truth — the seeded baseline normally covers this). No history →
  // fallback (the current person doc — legacy behavior, byte-identical to today).
  function resolveAt(hist, tMs, fallbackBasis) {
    if (!hist || !hist.length) return fallbackBasis;
    let found = null;
    for (let i = 0; i < hist.length; i++) {
      if (hist[i].effMs <= tMs) found = hist[i];
      else break;
    }
    return (found || hist[0]).basis;
  }

  // Material pay-basis changes whose effectiveFrom lands in [startMs, endMs].
  // Baseline entries never count as a change. Returns [{atMs, setAtMs, before,
  // after, fields, setBy}] chronological.
  //
  // BACKDATED APPENDS ARE DELIBERATE. The rules enforce append-only (prior
  // entries immutable) but NOT monotonic effectiveFrom — a raise entered late
  // with its TRUE effective date, or a correction re-stated at the original
  // conversion moment (Brian's 37→35 blip), is exactly the workflow this
  // feature exists for; forcing effectiveFrom == entry time would re-create
  // the original defect. The trail stays honest because setAt/setBy on the
  // appended entry are immutable — a backdated append is VISIBLE, not silent,
  // and consumers flag it when it lands in an already-closed period
  // (enteredAfterPeriodEnd below).
  function changesInWindow(hist, startMs, endMs) {
    const out = [];
    if (!hist || hist.length < 2) return out;
    for (let i = 1; i < hist.length; i++) {
      const cur = hist[i];
      if (cur.effMs === -Infinity) continue;
      if (cur.effMs < startMs || cur.effMs > endMs) continue;
      const prev = hist[i - 1].basis;
      if (basisEquals(prev, cur.basis)) continue;
      const fields = [];
      ['employmentType', 'hourlyRate', 'annualSalary', 'perDiem', 'payRate'].forEach(k => {
        if (prev[k] !== cur.basis[k]) fields.push(k);
      });
      out.push({ atMs: cur.effMs, setAtMs: cur.setAtMs || 0, before: prev, after: cur.basis, fields, setBy: cur.setBy });
    }
    return out;
  }

  // Split ONE entry's hours at every effectiveFrom that lands strictly inside
  // (inMs, outMs). Hours (which may differ from wall time due to adjustments)
  // are allocated proportionally to wall-clock time — split at the timestamp,
  // never rounded to the day. Degenerate windows resolve at inMs.
  function segmentEntry(hist, inMs, outMs, hours, fallbackBasis) {
    const h = num(hours) || 0;
    if (!hist || !hist.length || outMs === null || outMs === undefined || !(outMs > inMs)) {
      return [{ basis: resolveAt(hist, inMs, fallbackBasis), hours: h, fromMs: inMs, toMs: outMs }];
    }
    const cuts = hist
      .map(e => e.effMs)
      .filter(t => t !== -Infinity && t > inMs && t < outMs)
      .sort((a, b) => a - b);
    if (!cuts.length) {
      return [{ basis: resolveAt(hist, inMs, fallbackBasis), hours: h, fromMs: inMs, toMs: outMs }];
    }
    const bounds = [inMs, ...cuts, outMs];
    const totalMs = outMs - inMs;
    const segs = [];
    for (let i = 0; i < bounds.length - 1; i++) {
      const from = bounds[i], to = bounds[i + 1];
      if (!(to > from)) continue;
      segs.push({
        basis: resolveAt(hist, from, fallbackBasis),
        hours: h * ((to - from) / totalMs),
        fromMs: from,
        toMs: to,
      });
    }
    return segs;
  }

  // ── week helpers — TASK-1507: the COMPANY workweek ───────────────────────
  // Delegates to window.TenantLocalTime (utils/tenantLocalTime.js), the one
  // workweek helper clock.js and bi.js also use, so payroll, Clock and BI
  // bucket an hour into the same week: 12:00 AM on the company's workweek start
  // day in the COMPANY zone, never the viewer's phone zone. `tz` (optional)
  // pins the zone; omitted → the loaded company settings.
  // The device-local branch below is ONLY for a Node test that never loaded
  // tenantLocalTime.js — the browser always has it (app.js imports it before
  // any view).
  function _tlt() { return (typeof globalThis !== 'undefined' && globalThis.TenantLocalTime) || null; }
  function weekStartMs(tMs, weekStartDay, tz) {
    const T = _tlt();
    if (T) {
      const r = T.weekRange(tMs, weekStartDay || 0, tz ? { tz } : undefined);
      return r ? r.startMs : NaN;
    }
    const d = new Date(tMs);
    let diff = d.getDay() - (weekStartDay || 0);
    if (diff < 0) diff += 7;
    d.setDate(d.getDate() - diff);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }
  function weekKeyOf(tMs, weekStartDay, tz) {
    const T = _tlt();
    if (T) return T.weekKey(tMs, weekStartDay || 0, tz ? { tz } : undefined);
    const d = new Date(weekStartMs(tMs, weekStartDay));
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  // Mon–Fri workday accounting for the salary proration rule. Returns
  // { total, salaried } where a weekday wholly before the cutover counts 1 and
  // the cutover's own weekday counts 0.5 when the switch landed mid-day.
  function workdaySplit(periodStartMs, periodEndMs, cutoverMs, tz) {
    let total = 0, salaried = 0;
    periodDays(periodStartMs, periodEndMs, tz).forEach(({ dow, startMs: dayStart, endMs: dayEnd }) => {
      if (dow >= 1 && dow <= 5) {
        total += 1;
        if (cutoverMs !== null && cutoverMs !== undefined) {
          if (dayEnd <= cutoverMs) salaried += 1;
          else if (dayStart < cutoverMs && cutoverMs < dayEnd) salaried += 0.5;
        }
      }
    });
    return { total, salaried };
  }

  // TASK-1507 — the period's calendar days, each { dow, startMs, endMs } with
  // endMs = the next day's start (a DST day is 23 h or 25 h, never "+86400000").
  // With TenantLocalTime: the COMPANY dates from the one holding periodStartMs
  // to the one holding periodEndMs. Without it (Node tests only): device days.
  function periodDays(periodStartMs, periodEndMs, tz) {
    const out = [];
    const T = _tlt();
    if (T) {
      const o = tz ? { tz } : undefined;
      const last = T.companyYmd(periodEndMs, o);
      let ymd = T.companyYmd(periodStartMs, o);
      let guard = 0;
      while (ymd && last && ymd <= last && guard++ < 400) {
        const next = T.addDaysYmd(ymd, 1);
        const [y, m, dd] = ymd.split('-').map(Number);
        out.push({ dow: new Date(Date.UTC(y, m - 1, dd)).getUTCDay(), startMs: T.dayStartMs(ymd, o), endMs: T.dayStartMs(next, o) });
        ymd = next;
      }
      return out;
    }
    const d = new Date(periodStartMs);
    d.setHours(0, 0, 0, 0);
    let guard = 0;
    while (d.getTime() <= periodEndMs && guard++ < 400) {
      const startMs = d.getTime();
      const dow = d.getDay();
      d.setDate(d.getDate() + 1);
      out.push({ dow, startMs, endMs: d.getTime() });
    }
    return out;
  }

  // ── display helpers (shared by CSV / prep / print so wording can't drift) ─
  function fmtMoney(n) { return '$' + (Number(n) || 0).toFixed(2); }
  function describeBasis(b) {
    if (!b) return '—';
    if (isSalaryBasis(b)) return b.annualSalary ? `Salary ${'$' + Number(b.annualSalary).toLocaleString()}` : 'Salary';
    if (b.hourlyRate) return `${fmtMoney(b.hourlyRate)}/hr`;
    if (b.payRate) return `${fmtMoney(b.payRate)} ${b.payRateUnit || 'piece rate'}`;
    return '—';
  }
  function describeChange(chg) {
    const when = new Date(chg.atMs).toLocaleString('en-US', {
      month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
    });
    // Per-diem-only edits get their own wording — the wage basis didn't move.
    const wageFields = chg.fields.filter(f => f !== 'perDiem');
    if (!wageFields.length && chg.fields.includes('perDiem')) {
      return `per diem ${fmtMoney(chg.before.perDiem || 0)} → ${fmtMoney(chg.after.perDiem || 0)} eff ${when}`;
    }
    return `${describeBasis(chg.before)} → ${describeBasis(chg.after)} eff ${when}`;
  }

  // ── TASK-9388 — internal-time pay rates ───────────────────────────────────
  // A tenant may pay time clocked to an INTERNAL customer (Shop / Yard / …,
  // flagged internalTimeOnly in CRM) at its own $/hr. The rate lives in
  // tenants/{tid}/payConfig/internalTime (server-written, append-only
  // rateHistory per customer: [{rate, effectiveFrom, setAt, setBy}]).
  // effectiveFrom is the SERVER time of the save, so a rate is NEVER applied
  // retroactively: time before the first entry resolves to NO internal rate
  // (the person's own rate pays it) — unlike a person's pay history, whose
  // seeded baseline covers the past. rate null/0 = "rate removed".
  function normalizeInternalRateHistory(raw) {
    const arr = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.rateHistory) ? raw.rateHistory : []);
    return arr
      .map(e => ({ effMs: toMs(e && e.effectiveFrom), setAtMs: toMs(e && e.setAt) || 0, rate: num(e && e.rate) }))
      .filter(e => e.effMs !== null)
      .sort((a, b) => (a.effMs - b.effMs) || (a.setAtMs - b.setAtMs));
  }

  function internalRateAt(ihist, tMs) {
    if (!ihist || !ihist.length) return null;
    let found = null;
    for (let i = 0; i < ihist.length; i++) {
      if (ihist[i].effMs <= tMs) found = ihist[i];
      else break;
    }
    return found && found.rate > 0 ? found.rate : null;
  }

  // Split already-built segments again at extra cut times (an internal rate
  // change landing mid-shift), hours proportional to wall time — the same
  // rule segmentEntry uses for a person's rate change.
  function splitSegmentsAt(segs, cutTimes) {
    const cuts = (cutTimes || []).filter(t => Number.isFinite(t)).sort((a, b) => a - b);
    if (!cuts.length) return segs;
    const out = [];
    segs.forEach(s => {
      const from = s.fromMs, to = s.toMs;
      if (to === null || to === undefined || !(to > from)) { out.push(s); return; }
      const inner = cuts.filter(t => t > from && t < to);
      if (!inner.length) { out.push(s); return; }
      const bounds = [from, ...inner, to];
      const span = to - from;
      for (let i = 0; i < bounds.length - 1; i++) {
        const a = bounds[i], b = bounds[i + 1];
        if (!(b > a)) continue;
        out.push({ basis: s.basis, hours: s.hours * ((b - a) / span), fromMs: a, toMs: b });
      }
    });
    return out;
  }

  // ── ONE source of truth for OT HOURS (TASK-9388 COD, Independent) ──────────
  // computeCrossPeriodOT (clock.js) decides HOW MANY hours of each workweek are
  // regular-paid-this-period (week.reg) and overtime-paid-this-period (week.ot).
  // This engine never uses a threshold and never counts OT itself: it only
  // decides WHICH pieces those counts land on, chronologically —
  //   • regular: the EARLIEST week.reg hours among this period's pieces (last
  //     period's hours come first in the week, so they were the ones already
  //     paid as regular there);
  //   • overtime: the LAST week.ot hours of the whole workweek (the hours past
  //     the threshold, incl. carried-in ones when the week ends here).
  // The two never overlap (reg ⊂ [before, threshold), OT = [threshold, end)).
  // allocatePayable marks p.regPaid / p.otPaid and reports any hours it could
  // not place (a mismatch is surfaced, never silently priced).
  function allocatePayable(pieces, otW) {
    const sorted = (pieces || []).map((p, i) => ({ p, i }))
      .sort((a, b) => (a.p.fromMs - b.p.fromMs) || (a.i - b.i)).map(x => x.p);
    let regLeft = Math.max(0, num(otW.reg) || 0);
    sorted.forEach(p => {
      p.regPaid = 0; p.otPaid = 0;
      if (!p.inPeriod || regLeft <= EPS) return;
      const take = Math.min(p.hours, regLeft);
      p.regPaid = take; regLeft -= take;
    });
    let otLeft = Math.max(0, num(otW.ot) || 0);
    for (let i = sorted.length - 1; i >= 0 && otLeft > EPS; i--) {
      const p = sorted[i];
      const take = Math.min(p.hours - p.regPaid, otLeft);
      if (take > EPS) { p.otPaid = take; otLeft -= take; }
    }
    return { regUnplaced: regLeft, otUnplaced: otLeft };
  }

  // 29 CFR 778.419 — OT at 1.5x the rate of the work PERFORMED during the OT
  // hours: the premium on each piece's otPaid hours (placed by allocatePayable
  // from computeCrossPeriodOT's count) at that piece's rate. Refuses (ok:false →
  // the caller falls back to the weighted average and says so) when an OT hour
  // lands on salaried time or the OT hours could not all be placed.
  function rateOfWorkPremium(pieces, otHours) {
    let sumOt = 0, premium = 0;
    const byRate = {};
    for (const p of (pieces || [])) {
      const h = p.otPaid || 0;
      if (!(h > EPS)) continue;
      if (p.salaried) return { ok: false, why: 'an overtime hour fell on salaried time' };
      sumOt += h;
      premium += 0.5 * p.rate * h;
      const rk = p.rate.toFixed(2);
      byRate[rk] = (byRate[rk] || 0) + h;
    }
    if (Math.abs(sumOt - otHours) > 0.01) return { ok: false, why: 'overtime hours did not reconcile' };
    return {
      ok: true,
      premium,
      otByRate: Object.keys(byRate).sort((a, b) => parseFloat(a) - parseFloat(b))
        .map(rk => ({ rate: parseFloat(rk), hours: byRate[rk] })),
    };
  }

  // TASK-9388 COD (Domain) — the OT method is FORWARD-ONLY: a method applies to
  // workweeks that START at or after its effectiveFrom (29 CFR 778.419 needs
  // the agreement to predate the work). history: [{method, effectiveFrom}].
  function normalizeOtMethodHistory(raw) {
    return (Array.isArray(raw) ? raw : [])
      .map(e => ({ effMs: toMs(e && e.effectiveFrom), method: e && e.method === 'rate_of_work' ? 'rate_of_work' : 'weighted_average' }))
      .filter(e => e.effMs !== null)
      .sort((a, b) => a.effMs - b.effMs);
  }
  function otMethodForWeek(mh, weekStart) {
    let m = 'weighted_average';
    for (const e of mh) { if (e.effMs <= weekStart) m = e.method; else break; }
    return m;
  }

  // ── the engine ────────────────────────────────────────────────────────────
  // Prices ONE person's pay period.
  //   entries  — the person's WEEK-EXPANDED clock entries:
  //              [{inMs, outMs, hours, completed}] (completed only are priced)
  //   otWeeks  — computeCrossPeriodOT(...).weeks — the AUTHORITATIVE per-week
  //              hour counts (key, reg, ot, endsInPeriod, skipped). This engine
  //              never re-derives OT hours and holds no threshold: straight
  //              time is priced on exactly week.reg + week.ot hours, placed on
  //              pieces chronologically by allocatePayable (above). A week row
  //              without a numeric `reg` (legacy callers) pays every in-period
  //              hour straight, as the 1558 engine did.
  //   history  — raw payRates history array (or the doc), may be null/empty
  //   person   — current person doc (fallback basis + legacy behavior)
  //   opts     — { periodStartMs, periodEndMs (COMPANY-zone instants — clock.js
  //                passes TenantLocalTime.periodBoundsMs), weekStartDay,
  //                timeZone (optional; omitted → company settings), periodType,
  //                otMethodHistory ([{method, effectiveFrom}] — forward-only,
  //                resolved at each workweek's START), or legacy otMethod
  //                (applies to every week; tests/old callers only) }
  //   internalRates — TASK-9388: { [internalKey]: rateHistory } for internal
  //              customers paid at their own rate. An entry opts in by
  //              carrying `internalKey` — the CALLER decides (explicit-flag
  //              customer, person not excluded, not salaried). Absent → the
  //              result is identical to the TASK-1558 engine.
  // Returns { supported, salaryOnly, multiRate, basisChanged, changes,
  //           straightPay, salaryDollars, otPremium, gross,
  //           segments: [{rate, hours}] (in-period hourly rollup),
  //           weeks: [{key, fullHours, hourlyStraightFull, salaryDollars,
  //                    weightedRate, otHours, premium, exemptSalaried}],
  //           notes: [] }
  function computePeriodPay(args) {
    const { entries, otWeeks, history, person, opts, internalRates } = args || {};
    const o = opts || {};
    const methodHist = Array.isArray(o.otMethodHistory) ? normalizeOtMethodHistory(o.otMethodHistory) : null;
    const otMethod = methodHist
      ? (methodHist.length ? methodHist[methodHist.length - 1].method : 'weighted_average')
      : (o.otMethod === 'rate_of_work' ? 'rate_of_work' : 'weighted_average');
    const methodForWeek = (startMs) => methodHist ? otMethodForWeek(methodHist, startMs) : otMethod;
    const internalHists = {};
    Object.keys(internalRates || {}).forEach(k => {
      const h = normalizeInternalRateHistory(internalRates[k]);
      if (h.length) internalHists[k] = h;
    });
    const pStart = o.periodStartMs, pEnd = o.periodEndMs;
    const wsd = o.weekStartDay || 0;
    const wtz = o.timeZone || null;
    const hist = normalizeHistory(history);
    const fallback = basisFromPerson(person);
    const notes = [];
    const res = {
      supported: true, salaryOnly: false, multiRate: false, basisChanged: false,
      changes: [], straightPay: 0, salaryDollars: 0, otPremium: 0, gross: null,
      segments: [], weeks: [], notes,
      // TASK-9388 — the tenant's OT method, whether any internal rate priced
      // anything here, and in-period internal straight time per key + rate.
      otMethod, internalRated: false, internalSegments: [],
    };
    if (pStart === undefined || pEnd === undefined) { res.supported = false; notes.push('missing period bounds'); return res; }

    res.changes = changesInWindow(hist, pStart, pEnd);
    // A change whose effectiveFrom lands in this period but which was ENTERED
    // after the period ended retroactively alters a number that may already be
    // on a cut check. Backdating is legitimate (late entry / correction) — but
    // it must be loud, never silent (Code Reviewer 2026-08-12: append-
    // backdating is retroactive mutation; the mitigation is visibility, since
    // denying it would re-create the original defect for late entries).
    res.changes.forEach(c => { c.enteredAfterPeriodEnd = (c.setAtMs || 0) > pEnd; });
    if (res.changes.some(c => c.enteredAfterPeriodEnd)) {
      notes.push('a rate change affecting this period was ENTERED after the period closed — if this period was already paid, verify the difference against what was paid');
    }
    const basisFlips = res.changes.filter(c => c.fields.includes('employmentType'));
    res.basisChanged = basisFlips.length > 0;

    // Piece-rate people are not priceable here.
    if ((person && person.payType === 'pieceRate') || (!hist.length && fallback.payRate && !fallback.hourlyRate && !isSalaryBasis(fallback))) {
      res.supported = false;
      notes.push('piece-rate pay — review by hand');
      return res;
    }

    // Segment every completed entry; bucket by workweek.
    const weekMap = {}; // key -> { fullHours, hourlyStraightFull, salariedHoursFull, hourlyHoursFull, inPeriodHourlyStraight (= straight paid HERE), inPeriodSalariedHours, pieces }
    let sawSalarySeg = false, sawHourlySeg = false, sawUnratedHourly = false;
    (entries || []).forEach(e => {
      if (!e || e.completed === false) return;
      const inMs = toMs(e.inMs !== undefined ? e.inMs : e.clockIn);
      const outMs = toMs(e.outMs !== undefined ? e.outMs : e.clockOut);
      const hrs = num(e.hours !== undefined ? e.hours : e.totalHours) || 0;
      if (inMs === null || !hrs) return;
      const key = weekKeyOf(inMs, wsd, wtz);
      const w = weekMap[key] || (weekMap[key] = {
        fullHours: 0, hourlyStraightFull: 0, salariedHoursFull: 0, hourlyHoursFull: 0,
        inPeriodHourlyStraight: 0, inPeriodSalariedHours: 0, salaryDollars: 0,
        pieces: [], hasInternal: false, startMs: weekStartMs(inMs, wsd, wtz),
      });
      const inPeriod = inMs >= pStart && inMs <= pEnd; // start-date attribution, mirrors computeCrossPeriodOT
      const ikey = e.internalKey || null;
      const ihist = ikey ? (internalHists[ikey] || null) : null;
      let segs = segmentEntry(hist, inMs, outMs, hrs, fallback);
      if (ihist) segs = splitSegmentsAt(segs, ihist.map(x => x.effMs));
      segs.forEach(s => {
        w.fullHours += s.hours;
        const fromMs = Number.isFinite(s.fromMs) ? s.fromMs : inMs;
        if (isSalaryBasis(s.basis)) {
          sawSalarySeg = true;
          w.salariedHoursFull += s.hours;
          if (inPeriod) w.inPeriodSalariedHours += s.hours;
          w.pieces.push({ fromMs, hours: s.hours, rate: 0, salaried: true, internal: false, inPeriod, key: null });
        } else {
          sawHourlySeg = true;
          // TASK-9388 — an internal-rated piece pays that internal customer's
          // rate in effect at that moment; everything else the person's own.
          const iRate = ihist ? internalRateAt(ihist, fromMs) : null;
          const r = iRate !== null ? iRate : (s.basis && s.basis.hourlyRate ? s.basis.hourlyRate : 0);
          if (!r) sawUnratedHourly = true;
          w.hourlyHoursFull += s.hours;
          w.hourlyStraightFull += s.hours * r;
          w.pieces.push({ fromMs, hours: s.hours, rate: r, salaried: false, internal: iRate !== null, inPeriod, key: iRate !== null ? ikey : null });
          if (iRate !== null) w.hasInternal = true;
        }
      });
    });

    // Which straight-time hours does THIS period pay? Exactly the hours
    // computeCrossPeriodOT pays here (week.reg + week.ot), placed on pieces by
    // allocatePayable — so a straddling week never re-pays last period's
    // regular hours, a week ending next period defers its OT hours there, and
    // Σ paid hours == periodReg + periodOT (pinned by tests/task9388).
    const otByKey = {};
    (otWeeks || []).forEach(w => { if (w && !w.skipped) otByKey[w.key] = w; });
    const rateRollup = {}; // paid here: rate -> hours
    const internalRollup = {}; // paid here: 'key|rate' -> { key, rate, hours }
    res.payableHours = 0;
    Object.keys(weekMap).forEach(key => {
      const w = weekMap[key];
      const otW = otByKey[key];
      const counted = !!(otW && typeof otW.reg === 'number');
      if (counted) {
        const un = allocatePayable(w.pieces, otW);
        if (un.regUnplaced > 0.01 || un.otUnplaced > 0.01) {
          notes.push(`week of ${key}: ${(un.regUnplaced + un.otUnplaced).toFixed(2)} paid hours could not be matched to clock entries — review by hand`);
          res.supported = false;
        }
      }
      w.pieces.forEach(p => {
        const paid = counted ? (p.regPaid || 0) + (p.otPaid || 0) : (p.inPeriod ? p.hours : 0);
        if (!(paid > EPS)) return;
        res.payableHours += paid;
        if (p.salaried) return;
        w.inPeriodHourlyStraight += paid * p.rate;
        const rk = p.rate.toFixed(2);
        rateRollup[rk] = (rateRollup[rk] || 0) + paid;
        if (p.key) {
          const ik = p.key + '|' + rk;
          const slot = internalRollup[ik] || (internalRollup[ik] = { key: p.key, rate: parseFloat(rk), hours: 0 });
          slot.hours += paid;
        }
      });
    });

    res.segments = Object.keys(rateRollup)
      .sort((a, b) => parseFloat(a) - parseFloat(b))
      .map(rk => ({ rate: parseFloat(rk), hours: rateRollup[rk] }));
    res.multiRate = res.segments.length > 1 || (sawSalarySeg && sawHourlySeg);

    // Pure salary, no conversion → existing behavior (no gross; provider pays salary).
    if (sawSalarySeg && !sawHourlySeg) { res.salaryOnly = true; return res; }

    if (sawUnratedHourly) notes.push('some hours have no pay rate on file — they price at $0 until a rate is set');

    // Salary↔hourly conversion handling.
    if (sawSalarySeg && sawHourlySeg) {
      const flips = basisFlips;
      const toHourly = flips.filter(c => c.before.employmentType === 'salary' && c.after.employmentType !== 'salary');
      const toSalary = flips.filter(c => c.before.employmentType !== 'salary' && c.after.employmentType === 'salary');
      if (flips.length !== 1 || toHourly.length !== 1 || toSalary.length !== 0) {
        res.supported = false;
        notes.push('pay basis changed more than once (or converted TO salary) inside this period — review by hand');
        return res;
      }
      const conv = toHourly[0];
      const annual = num(conv.before.annualSalary);
      if (!annual) {
        res.supported = false;
        notes.push('salaried span has no annual salary on file — review by hand');
        return res;
      }
      const ppy = PERIODS_PER_YEAR[o.periodType] || 26;
      const wd = workdaySplit(pStart, pEnd, conv.atMs, wtz);
      if (!wd.total) {
        res.supported = false;
        notes.push('period contains no workdays — review by hand');
        return res;
      }
      const periodSalary = annual / ppy;
      res.salaryDollars = periodSalary * (wd.salaried / wd.total);
      // Attribute salary dollars to weeks by each week's salaried workdays
      // (walk the period's weekdays, assign each salaried day to its week key).
      const perWeekSalDays = {};
      periodDays(pStart, pEnd, wtz).forEach(({ dow, startMs: dayStart, endMs: dayEnd }) => {
        if (dow >= 1 && dow <= 5) {
          let frac = 0;
          if (dayEnd <= conv.atMs) frac = 1;
          else if (dayStart < conv.atMs && conv.atMs < dayEnd) frac = 0.5;
          if (frac > 0) {
            const wk = weekKeyOf(dayStart, wsd, wtz);
            perWeekSalDays[wk] = (perWeekSalDays[wk] || 0) + frac;
          }
        }
      });
      const salDayTotal = Object.values(perWeekSalDays).reduce((s, x) => s + x, 0);
      Object.keys(perWeekSalDays).forEach(wk => {
        if (!weekMap[wk]) weekMap[wk] = {
          fullHours: 0, hourlyStraightFull: 0, salariedHoursFull: 0, hourlyHoursFull: 0,
          inPeriodHourlyStraight: 0, inPeriodSalariedHours: 0, salaryDollars: 0,
          // TASK-1507 (2nd review): the week's own company start, not device noon.
          pieces: [], hasInternal: false, startMs: (_tlt() ? _tlt().weekRangeOfKey(wk, wtz ? { tz: wtz } : undefined).startMs : weekStartMs(new Date(wk + 'T12:00:00').getTime(), wsd)),
        };
        weekMap[wk].salaryDollars = salDayTotal > 0 ? res.salaryDollars * (perWeekSalDays[wk] / salDayTotal) : 0;
      });
    }

    // Price each workweek: straight time at segment rates + half-time premium on
    // the OT hours computeCrossPeriodOT attributed to THIS period, at the week's
    // weighted-average regular rate (29 CFR 778.115).
    let straight = 0, premium = 0;
    const weekRows = [];
    Object.keys(weekMap).sort().forEach(key => {
      const w = weekMap[key];
      const otW = otByKey[key];
      const exemptSalaried = w.salariedHoursFull > EPS && w.hourlyHoursFull <= EPS;
      const weekStraightFull = w.hourlyStraightFull + (w.salaryDollars || 0);
      const weightedRate = w.fullHours > EPS ? weekStraightFull / w.fullHours : 0;
      const otHours = (!exemptSalaried && otW) ? (otW.ot || 0) : 0;
      // Default, always lawful: 29 CFR 778.115 weighted-average regular rate.
      // TASK-9388 Option 1 (tenant setting; 29 CFR 778.419, needs a written
      // agreement made before the work) prices the OT hours at the rate of the
      // work performed during them — only in weeks that actually mix
      // internal-rated work in. A single-rate week prices the same either way.
      let wkPremium = 0.5 * weightedRate * otHours;
      let weekMethod = 'weighted_average';
      let otByRate = null;
      // Forward-only: the method in effect when THIS workweek started.
      const wkMethodSetting = methodForWeek(w.startMs);
      if (otHours > EPS && wkMethodSetting === 'rate_of_work' && w.hasInternal) {
        if (!(otW && typeof otW.reg === 'number')) allocatePayable(w.pieces, { reg: 0, ot: otHours });
        const r = rateOfWorkPremium(w.pieces, otHours);
        if (r.ok) {
          wkPremium = r.premium;
          weekMethod = 'rate_of_work';
          otByRate = r.otByRate;
        } else {
          notes.push(`week of ${key}: overtime priced at the weighted-average regular rate instead of the rate of work performed (${r.why})`);
        }
      }
      straight += w.inPeriodHourlyStraight;
      premium += wkPremium;
      if (w.hasInternal && otHours > EPS) res.internalRated = true;
      weekRows.push({
        key,
        fullHours: w.fullHours,
        hourlyStraightFull: w.hourlyStraightFull,
        salaryDollars: w.salaryDollars || 0,
        weightedRate,
        otHours,
        premium: wkPremium,
        exemptSalaried,
        method: weekMethod,
        otByRate,
        hasInternal: w.hasInternal,
      });
    });
    res.weeks = weekRows;
    res.straightPay = straight;
    res.otPremium = premium;
    res.gross = Math.round((straight + res.salaryDollars + premium) * 100) / 100;
    res.internalSegments = Object.keys(internalRollup).sort().map(k => internalRollup[k])
      .filter(x => x.hours > EPS)
      .map(x => ({ key: x.key, rate: x.rate, hours: x.hours, straight: x.hours * x.rate }));
    if (res.internalSegments.length) res.internalRated = true;
    if (res.multiRate) {
      notes.push(weekRows.some(w => w.method === 'rate_of_work')
        ? 'multiple pay rates in one workweek — OT at 1.5x the rate of the work performed during the OT hours (29 CFR 778.419; requires a written agreement made before the work)'
        : 'multiple pay rates in one workweek — OT premium uses the weighted-average regular rate (29 CFR 778.115)');
    }
    return res;
  }

  return {
    toMs,
    normalizeHistory,
    basisFromPerson,
    isSalaryBasis,
    resolveAt,
    changesInWindow,
    segmentEntry,
    workdaySplit,
    periodDays,
    describeBasis,
    describeChange,
    computePeriodPay,
    normalizeInternalRateHistory,
    internalRateAt,
    rateOfWorkPremium,
    allocatePayable,
    normalizeOtMethodHistory,
    otMethodForWeek,
    weekStartMs,
    weekKeyOf,
    PERIODS_PER_YEAR,
  };
});
